import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { ExtractedData } from '../../services/businessCardService';
import { Dot, Pill } from '../ui';

interface ExtractedDataPreviewProps {
  extractedData: ExtractedData;
  onFieldEdit: (field: string, value: string) => void;
}

type FieldKey = keyof Omit<ExtractedData, 'confidence'>;

interface FieldDef {
  key: FieldKey;
  label: string;
  placeholder?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  /** Social links carry no confidence score */
  scored?: boolean;
}

const GROUPS: Array<{ title: string; fields: FieldDef[] }> = [
  {
    title: 'Name',
    fields: [
      { key: 'firstName', label: 'First name', scored: true },
      { key: 'lastName', label: 'Last name', scored: true },
    ],
  },
  {
    title: 'Contact',
    fields: [
      { key: 'professionalEmail', label: 'Work email', inputMode: 'email', scored: true },
      { key: 'personalEmail', label: 'Personal email', inputMode: 'email', scored: true },
      { key: 'professionalPhone', label: 'Work phone', inputMode: 'tel', scored: true },
      { key: 'personalPhone', label: 'Personal phone', inputMode: 'tel', scored: true },
    ],
  },
  {
    title: 'Work',
    fields: [
      { key: 'role', label: 'Role', scored: true },
      { key: 'department', label: 'Department', scored: true },
      { key: 'organization', label: 'Organization', scored: true },
      { key: 'location', label: 'Location', scored: true },
    ],
  },
];

const SOCIAL: FieldDef[] = [
  { key: 'linkedinUrl', label: 'LinkedIn', placeholder: 'linkedin.com/in/…', inputMode: 'url' },
  { key: 'twitterUrl', label: 'X', placeholder: 'x.com/…', inputMode: 'url' },
  { key: 'facebookUrl', label: 'Facebook', placeholder: 'facebook.com/…', inputMode: 'url' },
  { key: 'telegramUrl', label: 'Telegram', placeholder: 't.me/…', inputMode: 'url' },
  { key: 'whatsapp', label: 'WhatsApp', placeholder: '+1234567890', inputMode: 'tel' },
  { key: 'website', label: 'Website', placeholder: 'example.com', inputMode: 'url' },
];

const tone = (confidence: number): 'green' | 'amber' => (confidence >= 80 ? 'green' : 'amber');

/**
 * ExtractedDataPreview — AI-extracted business card data as grouped,
 * editable label/value rows with a quiet confidence marker per field.
 * Every field is listed, including empty ones, so anything the AI
 * missed can be filled in.
 */
export const ExtractedDataPreview: React.FC<ExtractedDataPreviewProps> = ({ extractedData, onFieldEdit }) => {
  const hasSocial = SOCIAL.some(({ key }) => Boolean(extractedData[key]));
  const [socialOpen, setSocialOpen] = useState(hasSocial);
  const overall = extractedData.confidence.overall;

  const renderRow = (field: FieldDef) => {
    const value = (extractedData[field.key] as string | undefined) || '';
    const confidence = field.scored ? extractedData.confidence.fields[field.key] : undefined;
    const showConfidence = confidence !== undefined && (value !== '' || confidence > 0);
    const id = `extracted-${field.key}`;

    return (
      <div
        key={field.key}
        className="flex min-h-[44px] items-center gap-3 px-4 transition-colors focus-within:bg-neutral-50"
      >
        <label htmlFor={id} className="w-[104px] flex-shrink-0 text-[13px] text-neutral-500">
          {field.label}
        </label>
        <input
          id={id}
          type="text"
          value={value}
          onChange={(e) => onFieldEdit(field.key, e.target.value)}
          placeholder={field.placeholder ?? 'Add'}
          inputMode={field.inputMode}
          autoCapitalize={field.inputMode ? 'off' : undefined}
          autoCorrect={field.inputMode ? 'off' : undefined}
          spellCheck={field.inputMode ? false : undefined}
          className="h-11 min-w-0 flex-1 border-0 bg-transparent p-0 text-[16px] text-ink shadow-none placeholder:text-neutral-300 focus:outline-none focus:ring-0 sm:text-[13px]"
        />
        {showConfidence &&
          (confidence! < 60 ? (
            <Pill tone="amber" title={`${confidence}% confidence — check this field`}>
              Check
            </Pill>
          ) : (
            <span
              className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center"
              title={`${confidence}% confidence`}
              aria-label={`${confidence}% confidence`}
            >
              <Dot tone={tone(confidence!)} />
            </span>
          ))}
      </div>
    );
  };

  return (
    <div className="space-y-5">
      {overall < 60 ? (
        <p className="rounded-xl bg-amber-50 px-3.5 py-2.5 text-[13px] text-amber-700">
          Low confidence ({overall}%). Check each field.
        </p>
      ) : (
        <div className="flex items-center justify-between px-1 text-[12px] text-neutral-500">
          <span>Confidence</span>
          <span className="inline-flex items-center gap-1.5 tabular-nums text-ink">
            <Dot tone={tone(overall)} />
            {overall}%
          </span>
        </div>
      )}

      {GROUPS.map((group) => (
        <section key={group.title} aria-label={group.title}>
          <h3 className="px-1 pb-1.5 text-[12px] text-neutral-500">{group.title}</h3>
          <div className="divide-y divide-neutral-200/70 overflow-hidden rounded-2xl border border-neutral-200 bg-white">
            {group.fields.map(renderRow)}
          </div>
        </section>
      ))}

      <section aria-label="Social and web">
        <button
          type="button"
          onClick={() => setSocialOpen((open) => !open)}
          aria-expanded={socialOpen}
          className="flex w-full items-center justify-between rounded-lg px-1 pb-1.5 text-[12px] text-neutral-500 transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
        >
          <span>Social & web</span>
          <ChevronDown
            className={`h-4 w-4 text-neutral-400 transition-transform ${socialOpen ? 'rotate-180' : ''}`}
            strokeWidth={1.75}
          />
        </button>
        {socialOpen && (
          <div className="divide-y divide-neutral-200/70 overflow-hidden rounded-2xl border border-neutral-200 bg-white">
            {SOCIAL.map(renderRow)}
          </div>
        )}
      </section>
    </div>
  );
};
